/**
 * Zeigt Channex Task-IDs aus der Push-Historie als kompakte Chips mit Copy-Click.
 * Praktisch fuer Debugging (Channex-Support-Tickets) und Audit-Trail-Korrelation.
 *
 * Labels (`avail` / `restr`) helfen beim Auseinanderhalten welche Task-ID zu
 * welchem Channex-Endpoint gehoert. Convention im Backend: je Paket wird erst
 * /availability gepusht, dann /restrictions (siehe AriDispatchProcessor.Send.cs).
 * Wir leiten die Labels aus den Range-Daten ab.
 */
import { toast } from 'sonner';
import type { AriPushHistoryItem } from '@/shared/api';

interface Props {
  taskIds: string[];
  item: AriPushHistoryItem;
}

type Label = 'avail' | 'restr' | null;

// Die Kürzel bleiben intern; sichtbar ist die deutsche Bezeichnung (FRONTEND_GUIDELINES §11).
const LABEL_TEXT = { avail: 'Verfügbarkeit', restr: 'Beschränkungen' } as const;

function inferLabels(item: AriPushHistoryItem, count: number): Label[] {
  const hasAvail = item.available !== null;
  const hasRestr =
    item.rate !== null ||
    item.minStay !== null ||
    item.maxStay !== null ||
    item.closedToArrival !== null ||
    item.closedToDeparture !== null ||
    item.stopSell !== null;

  const inferred: Label[] = [];
  if (hasAvail) inferred.push('avail');
  if (hasRestr) inferred.push('restr');

  // Wenn Anzahl matcht (typischer Single-Range-Fall): inferred direkt nutzen.
  if (inferred.length === count) return inferred;

  // Multi-Range-Fallback: Range-Daten der ersten Range repraesentieren nicht
  // zwangslaeufig die Batch-Aggregation. Convention im Backend
  // (AriDispatchProcessor.Send.cs): [0] = availability, [1] = restrictions.
  if (count === 2) return ['avail', 'restr'];
  if (count === 1) {
    // 1 Task-ID + ambiguous → wenn Heuristik genau 1 Treffer hat, nutze ihn
    if (inferred.length === 1) return inferred;
    return [null];
  }
  return Array<Label>(count).fill(null);
}

export function TaskIdChips({ taskIds, item }: Props) {
  const labels = inferLabels(item, taskIds.length);
  const copyTaskId = (id: string) => {
    void navigator.clipboard.writeText(id);
    toast.success(`Vorgangskennung kopiert: ${id.slice(0, 8)}…`);
  };
  return (
    <>
      <span className="text-ink-faint font-semibold uppercase tracking-[0.12em]">
        Channex-Vorgänge:
      </span>
      {taskIds.map((id, i) => {
        const label = labels[i];
        return (
          <button
            key={id}
            type="button"
            onClick={() => copyTaskId(id)}
            className="border-line/50 text-ink-soft hover:border-ink-faint hover:text-ink inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono transition-colors"
            title={`${id} — zum Kopieren anklicken`}
          >
            {label && (
              <span className="text-ink-faint text-[9px] uppercase tracking-wider">
                {LABEL_TEXT[label]}
              </span>
            )}
            <span>{id.slice(0, 8)}</span>
          </button>
        );
      })}
    </>
  );
}
